<template>
  <Bar
    :chart-id="chartId"
    :chart-data="chartData"
    :chart-options="chartOptions"
    :dataset-id-key="datasetIdKey"
    :plugins="plugins"
    :css-classes="cssClasses"
    :styles="styles"
    :width="width"
    :height="height"
  />
</template>

<script>
import { Bar } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default {
  name: 'MemlistBarChart',
  components: { Bar },
  props: {
    chartId: { type: String, default: 'bar-chart' },
    datasetIdKey: { type: String, default: 'label' },
    width: { type: Number, default: 600 },
    height: { type: Number, default: 260 },
    cssClasses: { type: String, default: '' },
    styles: { type: Object, default: () => ({}) },
    plugins: { type: Array, default: () => [] },

    /* Data props */
    labels: { type: Array, required: true }, // X-axis labels
    datasets: { type: Array, required: true }, // Array of Chart.js dataset objects

    /* Optional options */
    title: { type: String, default: '' },
    showLegend: { type: Boolean, default: true },
    xAxisLabel: { type: String, default: '' },
    yAxisLabel: { type: String, default: '' }
  },

  computed: {
    chartData () {
      return {
        labels: this.labels,
        datasets: this.datasets.map(ds => ({
          borderWidth: 0,
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 18,
          categoryPercentage: 0.9,
          barPercentage: 0.9,
          ...ds
        }))
      }
    },

    chartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 8, right: 12, bottom: 8, left: 8 } },
        plugins: {
          legend: this.showLegend
            ? {
                position: 'bottom',
                labels: {
                  boxWidth: 10,
                  boxHeight: 10,
                  usePointStyle: true,
                  pointStyle: 'rounded'
                }
              }
            : { display: false },
          title: this.title
            ? { display: true, text: this.title, font: { size: 16, weight: '600' } }
            : { display: false },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed.y
                return ` ${ctx.dataset.label}: ${new Intl.NumberFormat().format(v)}`
              }
            }
          }
        },
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            title: this.xAxisLabel ? { display: true, text: this.xAxisLabel } : {},
            grid: { display: false },
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              autoSkipPadding: 8,
              callback: (val, index) => (index % 3 === 0 ? this.labels[index] : '')
            }
          },
          y: {
            title: this.yAxisLabel ? { display: true, text: this.yAxisLabel } : {},
            beginAtZero: true,
            grace: '5%',
            grid: {
              color: (ctx) => ctx.index === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
              drawBorder: false
            },
            ticks: {
              padding: 6,
              callback: (v) => {
                if (Math.abs(v) >= 1000) return (v/1000).toFixed(1).replace(/\.0$/, '') + 'k'
                return v
              }
            }
          }
        },
        animation: {
          duration: 400,
          easing: 'easeOutQuart'
        }
      }
    }
  }
}
</script>
